import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import DropDown from '../../components/DropDown/DropDown';
import { Footer } from '../../components/Footer';
import { Image } from '../../components/Image';
import MyAccountLayout from '../../components/MyAccountLayout/MyAccountLayout';
import PageTitle from '../../components/PageTitle';
import { Text } from '../../components/Text';
import { STYLE_VARS } from '../../constants/style-vars';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { DropDownItem } from '../../models/DropDownItem';
import Booking from './components/Booking';
import BookingCollapse from './components/BookingCollapse';
import { myBookingStyled as styled } from './styled';
import { BookingType, MyBookingsProps } from './types';
import { Button } from '../../components/Button';
import HorizontalTab from './components/HorizontalTab';
import dayjs from 'dayjs';
import { IBookedSport } from '../../types/sports.types';
import { IBookedEvent } from '../../types';
import { IBookedDining } from '../../types/dining.types';
import { AuthApi, GetMyBookingProps } from '../../services/api/auth.api';
import BookingCollapseSport from './components/BookingCollapseSport';
import BookingCollapseEvent from './components/BookingCollapseEvent';
import BookingCollapseDining from './components/BookingCollapseDining';
import { Loading } from '../../components';
import { Alert } from '../../services/alert';
import SportApi from '../../services/api/sports.api';
import BookingSport from './components/BookingSport';
import BookingEvent from './components/BookingEvent';
import BookingDining from './components/BookingDining';
import { NavigationService } from '../../services/navigation';

const MyBookings: FC<MyBookingsProps> = ({ navigation }) => {
  const isDesktop = useIsDesktop();
  const [value, setValue] = useState<DropDownItem>();
  const [sports, setSports] = useState<IBookedSport[]>([]);
  const [events, setEvents] = useState<IBookedEvent[]>([]);
  const [dinings, setDinings] = useState<IBookedDining[]>([]);
  const [privates, setPrivates] = useState<IBookedDining[]>([]);
  const [type, setType] = useState<'upcoming' | 'past'>('upcoming');
  const [currentTab, setCurrentTab] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);
  const [allCount, setAllCount] = useState<number>(0);

  const tabItems = [
    {
      name: 'All',
      value: 'all',
      width: '15%',
    },
    {
      name: 'Sports',
      value: 'sports',
      width: '20%',
    },
    {
      name: 'Events',
      value: 'events',
      width: '20%',
    },
    {
      name: 'Dining',
      value: 'dining',
      width: '20%',
    },
    {
      name: 'Function',
      value: 'private',
      width: '25%',
    },
  ];

  const getMyBooking = useCallback(async (props: GetMyBookingProps) => {
    setIsLoading(true);
    AuthApi.getMyBooking(props)
      .then((res) => {
        setSports(res.data.sports);
        setEvents(res.data.event);
        setDinings(res.data.dining);
        setPrivates(res.data.private);
        setAllCount(
          res.data.sports.length +
            res.data.event.length +
            res.data.dining.length +
            res.data.private.length
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleCancelBooking = useCallback(
    async (id: string | number, bookingType: string) => {
      Alert.alert(
        'Cancel',
        `Do you want to cancel your ${bookingType} booking?`,
        [
          {
            text: 'Back',
            onPress: () => {},
          },
          {
            text: 'Ok',
            onPress: () => {
              setIsCanceling(true);
              if (bookingType === 'sport') {
                SportApi.postCancelSport({
                  booking: id,
                  wait: 0,
                })
                  .then((res) => {
                    Alert.alert('Cancelation', 'Success');
                    getMyBooking({
                      type: type,
                    });
                  })
                  .catch((error) => {
                    Alert.alert('Error', error.message);
                  })
                  .finally(() => {
                    setIsCanceling(false);
                  });
              } else {
                AuthApi.cancelBooking({
                  id: id,
                  type: bookingType,
                })
                  .then((res) => {
                    Alert.alert('Cancelation', 'Success');
                    getMyBooking({
                      type: type,
                    });
                  })
                  .catch((error) => {
                    Alert.alert('Error', error.message[0]);
                  })
                  .finally(() => {
                    setIsCanceling(false);
                  });
              }
            },
          },
        ]
      );
    },
    [getMyBooking, type]
  );

  const upcomingBookingButton = useMemo(() => {
    return (
      <Button
        text="UPCOMING BOOKINGS"
        className={type === 'upcoming' ? 'green-button' : ''}
        style={
          type === 'upcoming'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        arrowRight={false}
        textStyle={{
          color: type === 'upcoming' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          setType('upcoming');
          getMyBooking({
            type: 'upcoming',
          });
        }}
      />
    );
  }, [type, getMyBooking]);

  const pastBookingButton = useMemo(() => {
    return (
      <Button
        text="PAST BOOKINGS"
        className={type === 'past' ? 'green-button' : ''}
        style={
          type === 'past'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        arrowRight={false}
        textStyle={{
          color: type === 'past' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          setType('past');
          getMyBooking({
            type: 'past',
          });
        }}
      />
    );
  }, [type, getMyBooking]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      setType('upcoming');
      getMyBooking({
        type: 'upcoming',
      });
    });

    return unsubscribe;
  }, [getMyBooking, navigation]);

  if (isDesktop()) {
    return (
      <MyAccountLayout
        title="My bookings"
        current={2}
        icon={require('../../assets/my-bookings.png')}
      >
        <View
          style={{
            alignItems: 'center',
            flexDirection: 'row',
            display: 'flex',
            gap: 10,
            width: '40%',
            minWidth: 350,
            marginBottom: 20,
          }}
        >
          <View style={{ flex: 1 }}>{upcomingBookingButton}</View>
          <View style={{ flex: 1 }}>{pastBookingButton}</View>
        </View>
        <View
          style={{
            marginBottom: 20,
          }}
        >
          <HorizontalTab
            current={currentTab}
            setCurrent={setCurrentTab}
            tabs={tabItems}
          />
        </View>
        <View>
          <View
            style={{
              flexDirection: 'row',
              paddingHorizontal: 20,
              paddingVertical: 15,
              backgroundColor: STYLE_VARS.kccBgGreen,
              // justifyContent:'space-between'
            }}
          >
            <View style={{ width: '35%', paddingLeft: 30 }}>
              <Text style={styled.bookingHeader}>Facility</Text>
            </View>
            <View style={{ width: '20%' }}>
              <Text style={styled.bookingHeader}>Date</Text>
            </View>
            <View style={{ width: '25%' }}>
              <Text style={styled.bookingHeader}>Time</Text>
            </View>
            <View style={{ width: '20%', alignItems: 'center' }}>
              <Text style={styled.bookingHeader}>Status</Text>
            </View>
          </View>
          {isLoading === true ? (
            <View style={{ marginTop: 20 }}>
              <Loading />
            </View>
          ) : (
            <>
              {(currentTab === 'all' || currentTab === 'sports') &&
                sports.map((sport) => (
                  <BookingCollapseSport
                    key={sport.bk_id}
                    bookingDetail={sport}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'sport');
                    }}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'events') &&
                events.map((event) => (
                  <BookingCollapseEvent
                    key={event.event_id}
                    bookingDetail={event}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'event');
                    }}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'dining') &&
                dinings.map((dining) => (
                  <BookingCollapseDining
                    key={dining.id}
                    bookingDetail={dining}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'dining');
                    }}
                    bookingType={'Dining'}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'private') &&
                privates.map((privateItem) => (
                  <BookingCollapseDining
                    key={privateItem.id}
                    bookingDetail={privateItem}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'private');
                    }}
                    bookingType={'Function'}
                    isPast={type === 'past'}
                  />
                ))}

              {((currentTab === 'all' && allCount === 0) ||
                (currentTab === 'dining' && dinings.length === 0) ||
                (currentTab === 'events' && events.length === 0) ||
                (currentTab === 'private' && privates.length === 0) ||
                (currentTab === 'sports' && sports.length === 0)) && (
                <View style={styled.noDataWrapper}>
                  <Text style={[styled.noDataText, { fontSize: 20 }]}>
                    THERE ARE CURRENTLY NO BOOKINGS
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
        {isCanceling === true && <Loading isFullScreen={true} />}
      </MyAccountLayout>
    );
  }

  return (
    <ScrollView nestedScrollEnabled={true}>
      <View style={styled.container}>
        <PageTitle
          title="My Bookings"
          icon={require('../../assets/my-bookings.png')}
          haveGoBackButton={true}
          handleGoBack={() => {
            NavigationService.push('Root', {
              screen: 'MyAccountTab',
            });
          }}
          style={{
            borderBottomWidth: 0,
            marginBottom: 0,
          }}
        />
      </View>
      <View style={[styled.container, { zIndex: 1000, position: 'relative' }]}>
        <View
          style={{
            paddingVertical: 20,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          {upcomingBookingButton}
          {pastBookingButton}
        </View>
        <View
          style={{
            marginBottom: 20,
          }}
        >
          <HorizontalTab
            current={currentTab}
            setCurrent={setCurrentTab}
            tabs={tabItems}
          />
        </View>
      </View>
      <View
        style={{
          marginBottom: 50,
          zIndex: 1,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            paddingHorizontal: 20,
            paddingVertical: 15,
            backgroundColor: STYLE_VARS.kccBgGreen,
            justifyContent: 'space-between',
          }}
        >
          <Text style={styled.bookingHeader}>Facility</Text>
          <Text style={styled.bookingHeader}>Time</Text>
        </View>
        <View style={styled.container}>
          {isLoading === true ? (
            <View style={{ marginTop: 20 }}>
              <Loading />
            </View>
          ) : (
            <>
              {(currentTab === 'all' || currentTab === 'sports') &&
                sports.map((sport) => (
                  <BookingSport
                    key={sport.bk_id}
                    booking={sport}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'sport');
                    }}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'events') &&
                events.map((event) => (
                  <BookingEvent
                    key={event.id}
                    booking={event}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'event');
                    }}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'dining') &&
                dinings.map((dining) => (
                  <BookingDining
                    key={dining.id}
                    booking={dining}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'dining');
                    }}
                    bookingType={'Dining'}
                    isPast={type === 'past'}
                  />
                ))}

              {(currentTab === 'all' || currentTab === 'private') &&
                privates.map((privateItem) => (
                  <BookingDining
                    key={privateItem.id}
                    booking={privateItem}
                    onCancelBooking={(id) => {
                      handleCancelBooking(id, 'private');
                    }}
                    bookingType={'Function'}
                    isPast={type === 'past'}
                  />
                ))}
              {((currentTab === 'all' && allCount === 0) ||
                (currentTab === 'dining' && dinings.length === 0) ||
                (currentTab === 'events' && events.length === 0) ||
                (currentTab === 'private' && privates.length === 0) ||
                (currentTab === 'sports' && sports.length === 0)) && (
                <View style={styled.noDataWrapper}>
                  <Text style={[styled.noDataText, { fontSize: 16 }]}>
                    THERE ARE CURRENTLY NO BOOKINGS
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
      </View>
      <Footer />
      {isCanceling === true && <Loading isFullScreen={true} />}
    </ScrollView>
  );
};

export default MyBookings;
